import { directMethodValue } from "@/services/payment/walkInPaymentMethod";
import { WalkInPaymentMethod } from "@/models/club";
import { useCheckSnappPayEligibility } from "@/services/payment/utils";
import {
  Alert,
  Box,
  Button,
  Card,
  DirectionProvider,
  Group,
  NumberInput,
  Paper,
  Progress,
  SegmentedControl,
  SimpleGrid,
  Slider,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import {
  IconBuildingBank,
  IconCash,
  IconCheck,
  IconCreditCard,
  IconInfoCircle,
  IconRefresh,
  IconWallet,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

type DirectMethod = "card" | "cash" | "transfer" | "other";

type Props = {
  directOnly?: boolean;
  onMethodChange?: (method: WalkInPaymentMethod) => void;
  totalAmount: number;
  customerSelected: boolean;
  onChange: (value: {
    directAmount: number;
    creditAmount: number;
    valid: boolean;
  }) => void;
};

const methods: Array<{
  value: DirectMethod;
  label: string;
  icon: typeof IconCreditCard;
}> = [
  { value: "card", label: "کارت بانکی", icon: IconCreditCard },
  { value: "cash", label: "نقدی", icon: IconCash },
  { value: "transfer", label: "کارت‌به‌کارت", icon: IconBuildingBank },
  { value: "other", label: "سایر", icon: IconWallet },
];

const presets = [25, 50, 75, 100];

const showDirectPaymentMethod = true;

export default function MobilePaymentSplit({
  directOnly = false,
  onMethodChange,
  totalAmount,
  customerSelected,
  onChange,
}: Props) {
  const maxCreditAmount = Math.max(0, totalAmount);
  const [method, setMethod] = useState<DirectMethod>("card");
  const [mode, setMode] = useState<"direct" | "split">("direct");
  const [creditAmount, setCreditAmount] = useState(0);
  const {
    mutate: checkEligibility,
    data: eligibility,
    isLoading,
    reset,
  } = useCheckSnappPayEligibility();

  useEffect(() => {
    setMethod("card");
    onMethodChange?.(directMethodValue.card);
    setMode("direct");
    setCreditAmount(0);
    reset();
  }, [reset, totalAmount, onMethodChange]);

  const directAmount = mode === "split" ? totalAmount - creditAmount : totalAmount;
  const creditPercent = totalAmount > 0 ? Math.round((creditAmount / totalAmount) * 100) : 0;
  const valid =
    totalAmount > 0 &&
    (mode === "direct" || (creditAmount > 0 && !!eligibility?.eligible));

  useEffect(() => {
    onChange({
      directAmount,
      creditAmount: mode === "split" ? creditAmount : 0,
      valid,
    });
  }, [creditAmount, directAmount, mode, onChange, valid]);

  const setSnappAmount = (amount: number) => {
    setCreditAmount(
      Math.min(
        Math.max(Number.isFinite(amount) ? amount : 0, 0),
        maxCreditAmount,
      ),
    );
    reset();
  };

  const chooseMode = (value: string) => {
    const nextMode = value as "direct" | "split";
    setMode(nextMode);
    reset();
    setCreditAmount(nextMode === "split" ? totalAmount : 0);
  };

  const presetAmount = (percent: number) =>
    Math.round((totalAmount * percent) / 100 / 5000) * 5000;

  const eligibilityMessage = useMemo(() => {
    if (!eligibility) return null;
    return eligibility.title ||
      (eligibility.eligible
        ? "اعتبار اسنپ‌پی مشتری تأیید شد"
        : "اعتبار کافی برای این مبلغ وجود ندارد");
  }, [eligibility]);

  return (
    <Card withBorder radius="xl" p="md" shadow="xs">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2}>
            <Text fw={800}>روش پرداخت</Text>
            <Text size="xs" c="dimmed">
              {directOnly ? "روش دریافت مبلغ را انتخاب کنید." : "ساده یا ترکیبی؛ هر طور مشتری راحت‌تر است"}
            </Text>
          </Stack>
          <Paper bg="blue.0" c="blue.8" radius="lg" px="sm" py={6}>
            <Text size="xs" fw={800} style={{ whiteSpace: "nowrap" }}>
              {totalAmount.toLocaleString()} ریال
            </Text>
          </Paper>
        </Group>

        {!directOnly && <SegmentedControl
          fullWidth
          radius="xl"
          size="md"
          value={mode}
          onChange={chooseMode}
          data={[
            { value: "direct", label: "پرداخت کامل" },
            { value: "split", label: "ترکیبی با اسنپ‌پی" },
          ]}
        />}

        {showDirectPaymentMethod && (
          <Stack gap="xs">
            <Text size="sm" fw={700}>روش دریافت مبلغ مستقیم</Text>
            <SimpleGrid cols={4} spacing="xs">
              {methods.map((item) => {
                const Icon = item.icon;
                const selected = method === item.value;
                return (
                  <UnstyledButton
                    key={item.value}
                    onClick={() => { setMethod(item.value); onMethodChange?.(directMethodValue[item.value]); }}
                    aria-label={item.label}
                  >
                    <Paper
                      withBorder
                      radius="lg"
                      p="xs"
                      bg={selected ? "blue.0" : "white"}
                      style={{
                        borderColor: selected ? "var(--mantine-color-blue-5)" : undefined,
                        transition: "transform 150ms ease, border-color 150ms ease",
                      }}
                    >
                      <Stack align="center" gap={5}>
                        <ThemeIcon variant={selected ? "filled" : "light"} radius="xl" size="md">
                          <Icon size={16} />
                        </ThemeIcon>
                        <Text size="10px" fw={selected ? 800 : 500} ta="center" lineClamp={1}>
                          {item.label}
                        </Text>
                      </Stack>
                    </Paper>
                  </UnstyledButton>
                );
              })}
            </SimpleGrid>
          </Stack>
        )}

        {mode === "split" && (
          <Paper radius="xl" p="md" bg="violet.0" withBorder style={{ borderColor: "var(--mantine-color-violet-2)" }}>
            <Stack gap="md">
              <Group justify="space-between">
                <Stack gap={0}>
                  <Text fw={800} c="violet.9">سهم اسنپ‌پی</Text>
                  <Text size="xs" c="dimmed">مبلغ موردنظر را مشخص و اعتبار را بررسی کنید</Text>
                </Stack>
                <ThemeIcon color="violet" variant="light" radius="xl" size="lg">
                  <IconWallet size={20} />
                </ThemeIcon>
              </Group>

              <NumberInput
                size="md"
                radius="md"
                min={0}
                max={maxCreditAmount}
                clampBehavior="strict"
                allowNegative={false}
                allowDecimal={false}
                step={5000}
                thousandSeparator="," 
                value={creditAmount}
                onChange={(value) => setSnappAmount(Number(value) || 0)}
                rightSection={<Text size="xs" c="dimmed" pr={4}>ریال</Text>}
                rightSectionWidth={56}
                styles={{
                  input: {
                    direction: "ltr",
                    textAlign: "left",
                    fontWeight: 800,
                    paddingLeft : 50,
                  },
                }}
              />
              
              <DirectionProvider initialDirection="ltr">
           <Slider
                color="violet"
                value={creditPercent}
                onChange={(value) => setSnappAmount(presetAmount(value))}
                min={0}
                max={100}
                step={5}
                label={(value) => `${value}٪`}
              />
              </DirectionProvider>

          

              <Group gap="xs" grow>
                {presets.map((percent) => (
                  <Button
                    key={percent}
                    size="compact-sm"
                    radius="xl"
                    variant={creditPercent === percent ? "filled" : "white"}
                    color="violet"
                    onClick={() => setSnappAmount(presetAmount(percent))}
                  >
                    {percent}٪
                  </Button>
                ))}
              </Group>

              <Box>
                <Group justify="space-between" mb={6}>
                  <Text size="xs" c="dimmed">مستقیم: {directAmount.toLocaleString()}</Text>
                  <Text size="xs" c="violet.8">اسنپ‌پی: {creditAmount.toLocaleString()}</Text>
                </Group>
                <Progress.Root size="lg" radius="xl">
                  <Progress.Section value={100 - creditPercent} color="blue" />
                  <Progress.Section value={creditPercent} color="violet" />
                </Progress.Root>
              </Box>

              <Button
                color={eligibility?.eligible ? "green" : "violet"}
                variant={eligibility?.eligible ? "light" : "filled"}
                radius="xl"
                size="md"
                loading={isLoading}
                disabled={!customerSelected || creditAmount <= 0}
                leftSection={eligibility?.eligible ? <IconCheck size={18} /> : <IconRefresh size={18} />}
                onClick={() => checkEligibility({ amount: creditAmount })}
              >
                {eligibility?.eligible ? "اعتبار تأیید شد" : "استعلام اعتبار اسنپ‌پی"}
              </Button>

              {!customerSelected && (
                <Alert color="yellow" icon={<IconInfoCircle size={17} />} radius="md" p="xs">
                  <Text size="xs">برای استعلام اعتبار، ابتدا مشتری را انتخاب کنید.</Text>
                </Alert>
              )}

              {eligibility && (
                <Alert
                  color={eligibility.eligible ? "green" : "red"}
                  icon={eligibility.eligible ? <IconCheck size={17} /> : <IconInfoCircle size={17} />}
                  radius="md"
                  p="xs"
                >
                  <Text size="xs" fw={700}>{eligibilityMessage}</Text>
                  {eligibility.description && <Text size="xs">{eligibility.description}</Text>}
                </Alert>
              )}

              <Text size="xs" c="dimmed" ta="center">
                پس از ثبت فاکتور، لینک پرداخت برای مشتری پیامک می‌شود.
              </Text>
            </Stack>
          </Paper>
        )}

        <Group justify="space-between" p="xs" bg={valid ? "green.0" : "gray.0"} style={{ borderRadius: 12 }}>
          <Group gap={6}>
            <ThemeIcon size="sm" radius="xl" color={valid ? "green" : "gray"}>
              <IconCheck size={13} />
            </ThemeIcon>
            <Text size="xs" fw={700} c={valid ? "green.8" : "dimmed"}>
              {valid ? "آماده ثبت فاکتور" : "استعلام اعتبار را انجام دهید"}
            </Text>
          </Group>
          <Text size="xs" fw={800}>{totalAmount.toLocaleString()} ریال</Text>
        </Group>
      </Stack>
    </Card>
  );
}
